import { useCanViewEmails } from '../../../../helpers/useCanViewEmails';
import React, { useEffect, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { SingleValue } from 'react-select';
import lodash from 'lodash';

import { getFoldersThunk, removeSelectedFolder, selectShareFolder, switchShareModal } from '../../../../store/folders/foldersSlice';
import {
  COLLABORATIVE_FOLDER_TYPE,
  FILE_ACCESS_TYPE,
  FOLDER_TYPE,
  GROUP,
  GROUP_WITH_SUBGROUP,
  USER,
  USER_NEW,
} from '../../../../enums/TYPES';
import { ACCESS_TYPE, ACCESS_TYPE_ENUM, ACCESS_TYPE_KEYS } from '../../../../enums/ACCESS_ENUM';
import { SHARE_TYPE, GROUP_TYPE, SHARED_CONTENT_ENUM } from '../../../../enums/ENUMS';
import {
  getFolderAccessessById,
  getFolderById,
  patchFolderAccess,
  postAccessFolder,
  postArrayOfNewAccesses,
} from '../../../../api/foldersApi';
import ShareFolderModalGroupList from './GroupList';
import { useAppDispatch, useAppSelector } from '../../../../store/hooks';
import useOnClickOutside from '../../../../helpers/useOnClickOutside';
import BellIconFilled from '../../../../assets/icons/bell-button-filled.svg';
import { getGroupsThunk } from '../../../../store/groups/groupsSlice';
import { getUsersByRolesArray } from '../../../../api/usersApi';
import { postRenotify } from '../../../../api/notificationsApi';
import { RENOTIFY_BODY } from '../../../../enums/NOTIFICATION';
import { ContainedButton, OutlinedButton } from '../../../modules/Buttons';
import { deleteAccess } from '../../../../api/accessesApi';
import useDebounce from '../../../../helpers/useDebounce';
import { RolesLowerCase } from '../../../../enums/ROLES';
import BellIcon from '../../../../assets/icons/share-notify-off.svg';
import { ReactComponent as UsersIcon } from '../../../../assets/icons/CollaboratorsUsersIcon.svg';
import SearchInput from '../../../modules/SearchInput';
import { getGroups } from '../../../../api/groupsApi';
import RolePlate from '../../../modules/RolePlate';
import ShareFolderModalUserList from './UserList';
import css from './ShareFolderModal.module.scss';
import Modal from '../../../modules/Modal';
import { PATHS } from '../../../../enums';
import Avatar from '../../../modules/Avatar';
import { Tooltip } from '@mui/material';
import { useHotkeys } from 'react-hotkeys-hook';

type AccessType = {
  [key: string]: {
    access: string;
    doNotNotify?: boolean;
  };
};

type NewAccessType = {
  [key: string]: {
    targetType: string;
    targetId?: string;
    group?: string;
    user?: string;
    type: string;
    access: string;
    doNotNotify?: boolean;
  };
};

type TShareCollaborativeFolderModal = {
  onUpdate?: React.Dispatch<React.SetStateAction<object>>;
  setFetchTrigger: (n: number) => void;
};

export enum RenotifyIdType {
  GROUP = 'GROUP',
  USER = 'USER',
}

export type AccessMapJson = { id: string; access: string; subGroups: { id: string; access: string }[] };

const ShareCollaborativeFolderModal: React.FC<TShareCollaborativeFolderModal> = ({ onUpdate, setFetchTrigger }) => {
  const canViewEmails = useCanViewEmails();
  const searchResultBarRef = useRef(null);
  const searchGroupResultBarRef = useRef(null);
  const dispatch = useAppDispatch();
  const notification = useSnackbar();
  const { showShareWithMeModal, showShareModal, shareFolder, page, limit, sortOption, sort } = useAppSelector((state) => state.folderStore);
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const { groups: allGroups } = useAppSelector((state) => state.groupsStore);
  const [userAccessList, setUserAccessList] = useState<FILE_ACCESS_TYPE[]>([]);
  const [groupAccessList, setGroupAccessList] = useState<FILE_ACCESS_TYPE[]>([]);
  const [subGroupAccessList, setSubGroupAccessList] = useState<FILE_ACCESS_TYPE[]>([]);
  const [loading, setLoading] = useState(false);
  const [idsToRenotify, setIdsToRenotify] = useState<RENOTIFY_BODY | null>(null);
  const [renotifyAll, setRenotifyAll] = useState(false);
  const submitButtonRef = useRef<HTMLDivElement>(null);

  const [thisFolder, setThisFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>(null);
  const fetchFolder = async () => {
    setLoading(true);
    try {
      const res = await getFolderAccessessById(shareFolder?.id!);
      if (res.data) {
        setThisFolder(res.data);
      }
    } catch (error: any) {
      console.log(error);
      if (error?.response?.data?.message) notification.showMessage(error?.response?.data?.message);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if ((showShareModal || showShareWithMeModal) && shareFolder) {
      fetchFolder();
    }
  }, [showShareModal, showShareWithMeModal, shareFolder]);

  useEffect(() => {
    dispatch(getGroupsThunk());
  }, []);

  const switchNotifyAll = () => {
    setRenotifyAll(!renotifyAll);
  };

  const pushAllNewEntitiesToRenotify = () => {
    const users = [...idsToRenotify!.userIds];
    const groups = [...idsToRenotify!.groupIds];
    if (newUsersAccess) {
      const arrayOfNewUsers = Object.entries(newUsersAccess);
      arrayOfNewUsers.forEach((user) => {
        users.push(user[1].user!);
      });
    }
    if (newGroupAccess) {
      const arrayOfNewGroups = Object.entries(newGroupAccess);
      arrayOfNewGroups.forEach((user) => {
        groups.push(user[1].user!);
      });
    }
    if (userAccessList.length) {
      userAccessList.forEach((access) => {
        users.push(access.user.id);
      });
    }
    if (groupAccessList.length) {
      groupAccessList.forEach((access) => {
        groups.push(access.group.id);
        if (access.group.subgroups.length) {
          access.group.subgroups.forEach((sub) => {
            groups.push(sub.id);
          });
        }
      });
    }
    // @ts-ignore
    setIdsToRenotify({ ...idsToRenotify, userIds: users, groupIds: groups });
  };

  useEffect(() => {
    if (renotifyAll && idsToRenotify) {
      pushAllNewEntitiesToRenotify();
    } else if (thisFolder) {
      setIdsToRenotify({
        userIds: [],
        groupIds: [],
        contentId: thisFolder.id,
        contentType: SHARED_CONTENT_ENUM.SHARED_FOLDER,
      });
    }
  }, [renotifyAll, thisFolder]);

  useEffect(() => {
    if (thisFolder) {
      setIdsToRenotify({
        userIds: [],
        groupIds: [],
        contentId: thisFolder.id,
        contentType: SHARED_CONTENT_ENUM.SHARED_FOLDER,
      });
    }
  }, [thisFolder, thisFolder]);

  const addEntityToRenotify = (id: string, type: RenotifyIdType, includeSubgroups?: GROUP) => {
    if (type === RenotifyIdType.GROUP && idsToRenotify && includeSubgroups) {
      let groupIds = [...idsToRenotify.groupIds];
      if (groupIds.includes(id)) {
        groupIds = groupIds.filter((groupId) => !includeSubgroups.subgroups.map((g) => g.id).includes(groupId));
        groupIds = groupIds.filter((groupId) => groupId !== id);
      } else {
        groupIds = [...groupIds, id, ...includeSubgroups.subgroups.map((g) => g.id)];
      }
      setIdsToRenotify({
        ...idsToRenotify,
        groupIds,
      });
    } else if (type === RenotifyIdType.GROUP && idsToRenotify) {
      const groupIds = [...idsToRenotify.groupIds];
      if (groupIds.includes(id)) {
        const index = groupIds.indexOf(id);
        groupIds.splice(index, 1);
      } else {
        groupIds.push(id);
      }
      setIdsToRenotify({
        ...idsToRenotify,
        groupIds,
      });
    } else if (type === RenotifyIdType.USER && idsToRenotify) {
      const userIds = [...idsToRenotify.userIds];
      if (userIds.includes(id)) {
        const index = userIds.indexOf(id);
        userIds.splice(index, 1);
      } else {
        userIds.push(id);
      }
      setIdsToRenotify({
        ...idsToRenotify,
        userIds,
      });
    }
  };

  const [accessMapJson, setAccessMapJson] = useState<AccessMapJson[]>([]);

  // parse response to three different arrays
  const accessArrayParser = (data: FOLDER_TYPE) => {
    const userAccesses = data?.access.filter((item) => item?.type === SHARE_TYPE.USER);
    const groupAccesses = data?.access.filter((item) => item?.type === SHARE_TYPE.GROUP);

    const mainGroups = groupAccesses?.filter((item) => item?.group?.type === GROUP_TYPE.GROUP);
    const subGroups = groupAccesses?.filter((item) => item?.group?.type === GROUP_TYPE.SUBGROUP);
    if (subGroups) {
      let refetch = false;
      subGroups.forEach(async (subG) => {
        if (subG.group?.parent_group?.id && !mainGroups?.find((gr) => gr.group.id == subG.group?.parent_group?.id)) {
          await deleteAccess(subG.id);
          refetch = true;
        }
      });

      if (refetch) {
        setFetchTrigger(Date.now());
        if (onUpdate) {
          onUpdate({});
        }
      }
    }

    const groupsWithAccessess: any = [];
    mainGroups.forEach((g) => {
      const parentGroup = allGroups?.find((mainG) => mainG.id === g.group.id);
      // will fix "type any" later
      const obj: any = {
        id: g.group.id,
        access: g.access,
        subGroups: parentGroup?.subgroups.map((sub) => ({ id: sub.id, access: '' })) || [],
      };
      g.group.subgroups.forEach((sub) => {
        const subGroupAccess = subGroups.find((subAcc) => subAcc.group.id === sub.id);
        const indexOfSubgroup = obj.subGroups?.findIndex((s: { id: string; access: string }) => s.id === sub.id);
        if (indexOfSubgroup >= 0) {
          obj.subGroups[indexOfSubgroup] = {
            ...obj.subGroups[indexOfSubgroup],
            access: subGroupAccess?.access?.title,
          };
        }
      });
      groupsWithAccessess.push(obj);
    });

    const unsavedAccesses = accessMapJson.filter((parent) => newGroups.findIndex((g) => g.id === parent.id) >= 0);

    setAccessMapJson([...groupsWithAccessess, ...unsavedAccesses]);
    setSubGroupAccessList(subGroups);
    setGroupAccessList([...mainGroups]);

    const userAccessesModified = userAccesses.map((obj) => {
      const modified = lodash.cloneDeep(obj);

      if (modified.user && modified.user.organization_users) {
        const foundObject = modified.user.organization_users.find((ob: any) => ob.organization.id === myOrganization?.id);
        if (foundObject) {
          modified.user.role = foundObject.role;
        }
      }
      return modified;
    });
    setUserAccessList(userAccessesModified.filter((access) => access?.user?.id !== currentUser?.id));
  };

  // users search
  const [searchedUsers, setSearchedUsers] = useState<USER_NEW[]>([]);
  const [isUserSearchLoading, setIsUserSearchLoading] = useState(false);
  const [isUserSearchEmpty, setIsUserSearchEmpty] = useState(false);
  const [searchText, setSearchText] = useState('');
  const debounce = useDebounce<string>(searchText, 500);
  const onUserSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchText(value);
    setIsUserSearchLoading(value.trim().length > 0);
    setIsUserSearchEmpty(false);
  };
  const fetchSearch = async () => {
    setIsUserSearchLoading(true);
    setLoading(true);
    try {
      const ignoreUsers = [
        ...userAccessList.map((access) => access.user?.id),
        ...newUsers.map((obj) => obj.user.id),
        currentUser?.id || '',
      ];

      if (thisFolder?.collaborators) {
        ignoreUsers.push(...thisFolder.collaborators.map((u) => u.id));
      }

      const res = await getUsersByRolesArray(
        [RolesLowerCase.ANALYST, RolesLowerCase.VIEWER, RolesLowerCase.ORGANIZATION_ADMIN],
        searchText,
        ignoreUsers,
      );
      setSearchedUsers(res.documents);
      setIsUserSearchEmpty((res.documents || []).length === 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setIsUserSearchLoading(false);
    }
  };
  useEffect(() => {
    if (thisFolder) {
      accessArrayParser(thisFolder);
    }
  }, [thisFolder, allGroups]);
  useEffect(() => {
    if (!searchText && thisFolder && allGroups?.length) {
      setSearchedUsers([]);
      setIsUserSearchLoading(false);
      setIsUserSearchEmpty(false);
    } else if (searchText && thisFolder) {
      fetchSearch();
    }
  }, [debounce, thisFolder, allGroups]);

  // group search
  const [searchedGroups, setSearchedGroups] = useState<GROUP[]>([]);
  const [isGroupSearchLoading, setIsGroupSearchLoading] = useState(false);
  const [isGroupSearchEmpty, setIsGroupSearchEmpty] = useState(false);
  const [searchGroupText, setSearchGroupText] = useState('');
  const debounceGroup = useDebounce<string>(searchGroupText, 500);
  const onGroupSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchGroupText(value);
    setIsGroupSearchLoading(value.trim().length > 0);
    setIsGroupSearchEmpty(false);
  };
  const fetchSearchGroup = async () => {
    setIsGroupSearchLoading(true);
    setLoading(true);
    try {
      const ignoreGroups = [
        ...groupAccessList.map((access) => access?.group?.id),
        ...newGroups.map((group: GROUP) => group.id),
        ...subGroupAccessList.map((access) => access?.group?.id),
      ];
      const res = await getGroups({ search: searchGroupText });
      const resultArr = res.documents.filter((org: GROUP_WITH_SUBGROUP) => !ignoreGroups.includes(org.id));
      setSearchedGroups(resultArr);
      setIsGroupSearchEmpty(resultArr.length === 0);
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
      setIsGroupSearchLoading(false);
    }
  };

  useEffect(() => {
    if (!searchGroupText && thisFolder && allGroups?.length) {
      setSearchedGroups([]);
      setIsGroupSearchLoading(false);
      setIsGroupSearchEmpty(false);
    } else if (searchGroupText && thisFolder) {
      fetchSearchGroup();
    }
  }, [debounceGroup, thisFolder, allGroups]);

  // group access change
  const [groupAccess, setGroupAccess] = useState<AccessType>();
  const onGroupAccessChange = (
    accessId: string,
    access: SingleValue<{ value: string; label: ACCESS_TYPE }>,
    groupId: string,
    groupType: GROUP_TYPE,
    accessObject?: FILE_ACCESS_TYPE,
  ) => {
    const accessMapJsonCopied = lodash.cloneDeep(accessMapJson);
    const itsGroup = accessMapJsonCopied.find((g) => g.id === groupId);
    const itsSubgroup = accessMapJsonCopied.find((g) => g.subGroups.find((sub) => sub.id === groupId));
    if (itsGroup) {
      const indexOfGroup = accessMapJsonCopied.findIndex((g) => g.id === itsGroup.id);
      accessMapJsonCopied[indexOfGroup] = { ...accessMapJsonCopied[indexOfGroup], access: access?.value! };
      if (access?.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS) {
        accessMapJsonCopied[indexOfGroup].subGroups.forEach((sub, index) => {
          accessMapJsonCopied[indexOfGroup].subGroups[index] = {
            ...accessMapJsonCopied[indexOfGroup].subGroups[index],
            access: access?.value!,
          };
        });
      }
    } else if (itsSubgroup) {
      const indexOfGroup = accessMapJsonCopied.findIndex((g) => g.id === itsSubgroup.id);
      const indexOfSubgroup = accessMapJsonCopied[indexOfGroup].subGroups.findIndex((sub) => sub.id === groupId);
      accessMapJsonCopied[indexOfGroup].subGroups[indexOfSubgroup] = {
        id: groupId,
        access: access?.value!,
      };
    }
    setAccessMapJson(accessMapJsonCopied);

    const obj = {
      ...groupAccess,
      [accessId]: {
        access: access!.value,
      },
    };
    if (access?.value === ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS) {
      obj[accessId] = {
        ...obj[accessId],
        doNotNotify: true,
      };
    }

    if (
      access?.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS &&
      groupType === GROUP_TYPE.GROUP &&
      accessObject &&
      accessObject.group.subgroups
    ) {
      accessObject.group.subgroups.forEach((sub) => {
        const subgroupAccessObject = subGroupAccessList.find((subAcc) => subAcc.group.id === sub.id);
        if (subgroupAccessObject) {
          obj[subgroupAccessObject.id] = {
            access: access!.value,
          };
        } else {
          const newObj = {
            ...newGroupAccess,
            [sub.id]: {
              targetType: 'folder',
              targetId: thisFolder!.id,
              group: sub.id,
              type: 'group',
              access: access!.value,
            },
          };
          setNewGroupAccess((prev) => ({ ...prev, ...newObj }));
        }
      });
    }
    setGroupAccess((prev) => ({ ...prev, ...obj }));
  };

  // new group access change
  const [newGroups, setNewGroups] = useState<GROUP[]>([]);
  const [newGroupAccess, setNewGroupAccess] = useState<NewAccessType>();
  const onNewGroupAccessChange = (
    groupId: string,
    access: SingleValue<{ value: string; label: ACCESS_TYPE }>,
    groupType: GROUP_TYPE,
    groupObj?: GROUP_WITH_SUBGROUP,
  ) => {
    const accessMapJsonCopied = lodash.cloneDeep(accessMapJson);
    const itsGroup = accessMapJsonCopied.find((g) => g.id === groupId);
    const itsSubgroup = accessMapJsonCopied.find((g) => g.subGroups.find((sub) => sub.id === groupId));
    if (itsGroup) {
      const indexOfGroup = accessMapJsonCopied.findIndex((g) => g.id === itsGroup.id);
      accessMapJsonCopied[indexOfGroup] = { ...accessMapJsonCopied[indexOfGroup], access: access?.value! };
      if (access?.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS) {
        accessMapJsonCopied[indexOfGroup].subGroups.forEach((sub, index) => {
          accessMapJsonCopied[indexOfGroup].subGroups[index] = {
            ...accessMapJsonCopied[indexOfGroup].subGroups[index],
            access: access?.value!,
          };
        });
      }
    } else if (itsSubgroup) {
      const indexOfGroup = accessMapJsonCopied.findIndex((g) => g.id === itsSubgroup.id);
      const indexOfSubgroup = accessMapJsonCopied[indexOfGroup].subGroups.findIndex((sub) => sub.id === groupId);
      accessMapJsonCopied[indexOfGroup].subGroups[indexOfSubgroup] = {
        id: groupId,
        access: access?.value!,
      };
    } else if (groupType === GROUP_TYPE.GROUP) {
      const alreadyInMapJson = accessMapJsonCopied.find((g) => g.id === groupId);
      const parentGroup = allGroups.find((g) => g.id === groupId);
      if (alreadyInMapJson) {
        const indexOfGroup = accessMapJsonCopied.findIndex((g) => g.id === alreadyInMapJson.id);
        accessMapJsonCopied[indexOfGroup] = { ...accessMapJsonCopied[indexOfGroup], access: access?.value! };
        if (access?.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS) {
          accessMapJsonCopied[indexOfGroup].subGroups.forEach((sub, index) => {
            accessMapJsonCopied[indexOfGroup].subGroups[index] = {
              ...accessMapJsonCopied[indexOfGroup].subGroups[index],
              access: access?.value!,
            };
          });
        }
      } else if (parentGroup) {
        accessMapJsonCopied.push({
          id: groupId,
          access: access?.value!,
          subGroups: parentGroup.subgroups.map((s) => ({ id: s.id, access: access?.value! })),
        });
      }
    }
    setAccessMapJson(accessMapJsonCopied);

    setSearchedGroups([]);
    const obj = {
      ...newGroupAccess,
      [groupId]: {
        targetType: 'folder',
        targetId: thisFolder!.id,
        group: groupId,
        type: 'group',
        access: access!.value,
      },
    };
    if (access?.value === ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS) {
      obj[groupId] = {
        ...obj[groupId],
        doNotNotify: true,
      };
    }

    if (access?.value !== ACCESS_TYPE_ENUM.SUBGROUP_SPECIFIC_ACCESS && groupType === GROUP_TYPE.GROUP && groupObj) {
      groupObj.subgroups.forEach((sub) => {
        obj[sub.id] = {
          targetType: 'folder',
          targetId: thisFolder!.id,
          group: sub.id,
          type: 'group',
          access: access!.value,
        };
      });
    }
    setNewGroupAccess((prev) => ({ ...prev, ...obj }));
  };

  // users access change
  const [usersAccess, setUsersAccess] = useState<AccessType>();
  const onUserAccessChange = (accessId: string, access: SingleValue<{ value: string; label: ACCESS_TYPE }>) => {
    const obj = {
      ...usersAccess,
      [accessId]: {
        access: access!.value,
      },
    };
    setUsersAccess(obj);
  };

  // new users access change
  const [newUsers, setNewUsers] = useState<USER_NEW[]>([]);
  const [newUsersAccess, setNewUsersAccess] = useState<NewAccessType>();
  const onNewUserAccessChange = (userId: string, access: SingleValue<{ value: string; label: ACCESS_TYPE }>) => {
    setSearchedUsers([]);
    const obj = {
      ...newUsersAccess,
      [userId]: {
        targetType: 'folder',
        targetId: thisFolder!.id,
        user: userId,
        type: 'user',
        access: access!.value,
      },
    };
    setNewUsersAccess(obj);
  };

  const [notify, setNotify] = useState(false);
  const [messageTextForSharedContent, setMessageText] = useState('');
  const switchNotify = () => {
    setNotify(!notify);
    setMessageText('');
  };
  const onMessageTextInput = (e: any) => {
    setMessageText(e.target.value);
  };

  // submit
  const createUrl = (idOfFolder?: string) => {
    let result = `${PATHS.SHARED_VIDEOFEEDS_LIST.path}${idOfFolder ? `?folderId=${idOfFolder}` : ''}`;
    return result;
  };
  const onSubmit = async (e?: React.FormEvent<HTMLFormElement>) => {
    const redirectUrlToSend = createUrl(thisFolder?.id);

    e?.preventDefault();
    setLoading(true);
    try {
      if (groupAccess) {
        for (const [groupId, group] of Object.entries(groupAccess)) {
          if (group.access === ACCESS_TYPE_ENUM.NO_ACCESS) {
            try {
              await patchFolderAccess({
                accessId: groupId,
                access: ACCESS_TYPE_ENUM.WITHOUT_ACCESS,
                notify: false,
                contentUrl: '',
                messageTextForSharedContent,
              });
            } catch (err) {
              console.log(err);
            }
          } else {
            await patchFolderAccess({
              accessId: groupId,
              access: group.access as ACCESS_TYPE,
              notify: group?.doNotNotify ? false : notify,
              contentUrl: redirectUrlToSend,
              messageTextForSharedContent,
            });
          }
        }
      }

      if (usersAccess) {
        for (const [userId, user] of Object.entries(usersAccess)) {
          await patchFolderAccess({
            accessId: userId,
            access: user.access as ACCESS_TYPE,
            notify,
            contentUrl: redirectUrlToSend,
            messageTextForSharedContent,
          });
        }
      }

      if (newUsersAccess) {
        const newUsersArr = Object.entries(newUsersAccess);
        await postArrayOfNewAccesses(
          newUsersArr.map((user) => ({
            ...user[1],
            notify,
            contentUrl: redirectUrlToSend,
            messageTextForSharedContent,
          })),
        );
      }

      if (newGroupAccess) {
        const newGroupsArr = Object.entries(newGroupAccess);
        await postArrayOfNewAccesses(
          newGroupsArr.map((group) => ({
            ...group[1],
            notify: group[1]?.doNotNotify ? false : notify,
            contentUrl: redirectUrlToSend,
            messageTextForSharedContent,
          })),
        );
      }

      if (idsToRenotify && (idsToRenotify.userIds.length || idsToRenotify?.groupIds.length)) {
        await postRenotify(idsToRenotify, redirectUrlToSend, messageTextForSharedContent);
      }
    } catch (err) {
      console.log(err);
    } finally {
      setTimeout(() => {
        // without this settimeout, sometimes data that cames from fetch GET request is old data
        setFetchTrigger(Date.now());
        setNotify(false);
        closeModal();
        if (onUpdate) {
          onUpdate({});
        }
        setIdsToRenotify(null);
        setLoading(false);
      }, 2000);
    }
  };

  const closeModal = () => {
    dispatch(removeSelectedFolder());
    dispatch(switchShareModal(false));
    setSearchedUsers([]);
    setSearchText('');
    setSearchedGroups([]);
    setSearchGroupText('');
    setUsersAccess(undefined);
    setGroupAccess(undefined);
    setNewUsers([]);
    setNewGroups([]);
    setUserAccessList([]);
    setGroupAccessList([]);
    setIdsToRenotify(null);
    setRenotifyAll(false);
    setAccessMapJson([]);
    setMessageText('');
  };

  const onNewUserClick = (user: USER_NEW, index: number) => {
    setNewUsers([user, ...newUsers]);
    searchedUsers.splice(index, 1);
    setSearchedUsers([...searchedUsers]);

    const obj = {
      ...newUsersAccess,
      [user.user.id]: {
        targetType: 'folder',
        targetId: thisFolder!.id,
        user: user.user.id,
        type: 'user',
        access: ACCESS_TYPE_KEYS[ACCESS_TYPE.COMMENT_AND_CREATE],
      },
    };
    setNewUsersAccess(obj);
  };

  const onNewGroupClick = (group: GROUP, index: number) => {
    setNewGroups([group, ...newGroups]);
    searchedGroups.splice(index, 1);
    setSearchedGroups([...searchedGroups]);

    const obj = {
      ...newGroupAccess,
      [group.id]: {
        targetType: 'folder',
        targetId: thisFolder!.id,
        group: group.id,
        type: 'group',
        access: ACCESS_TYPE_ENUM.COMMENT_AND_CREATE,
      },
    };

    const accessMapJsonCopied = lodash.cloneDeep(accessMapJson);
    if (group.subgroups.length) {
      group.subgroups.forEach((sub) => {
        obj[sub.id] = {
          targetType: 'folder',
          targetId: thisFolder!.id,
          group: sub.id,
          type: 'group',
          access: ACCESS_TYPE_ENUM.COMMENT_AND_CREATE,
        };
      });
      accessMapJsonCopied.push({
        id: group.id,
        access: ACCESS_TYPE_ENUM.COMMENT_AND_CREATE,
        subGroups: group.subgroups.map((s) => ({ id: s.id, access: ACCESS_TYPE_ENUM.COMMENT_AND_CREATE })),
      });
    }
    setNewGroupAccess(obj);
    setAccessMapJson(accessMapJsonCopied);
  };

  const onSearchResultsDismiss = () => {
    setSearchedUsers([]);
    setSearchText('');
  };

  const onGroupSearchResultsDismiss = () => {
    setSearchedGroups([]);
    setSearchGroupText('');
  };

  const handleDeleteAccess = async (accessId: string, accessType?: 'user' | 'group' | 'subgroup') => {
    try {
      if (accessType === 'group') {
        const subgroups = groupAccessList.find((obj) => obj.id == accessId)?.group?.subgroups.map((sub) => sub.id);
        subGroupAccessList.forEach(async (subAcc) => {
          if (subgroups?.find((id) => id == subAcc.group.id)) {
            await deleteAccess(subAcc.id);
          }
        });
      }

      if (accessType === 'subgroup') {
        await patchFolderAccess({
          accessId: accessId,
          access: ACCESS_TYPE_ENUM.WITHOUT_ACCESS,
          notify: false,
          contentUrl: '',
          messageTextForSharedContent,
        });
      } else {
        await deleteAccess(accessId);
      }

      setFetchTrigger && setFetchTrigger(Date.now());
      dispatch(getFoldersThunk({ page, limit, sortOption }));

      if (thisFolder) {
        const subGroupIds = (thisFolder.access.find((x) => x.id === accessId)?.group?.subgroups || []).map((y) => y.id);

        const newAccessList = thisFolder.access
          .filter((access) => access.id !== accessId)
          .filter((access) => !subGroupIds?.includes(access.group?.id));

        const newShareFolder = { ...thisFolder, access: newAccessList };
        dispatch(selectShareFolder({ ...newShareFolder }));
      }
      if (accessType === 'subgroup') {
        closeModal();
      }
    } catch (error: any) {
      notification.showMessage(error?.response?.data?.message);
    }
  };

  const handleDeleteNewUser = (userId: string) => {
    setNewUsers(newUsers.filter((user) => user.id !== userId));
    if (newUsersAccess) {
      delete newUsersAccess[userId];
      setNewUsersAccess(Object.keys(newUsersAccess).length !== 0 ? newUsersAccess : undefined);
    }
  };

  const handleDeleteNewGroup = (groupId: string) => {
    setNewGroups(newGroups.filter((group) => group.id !== groupId));
    if (newGroupAccess) {
      delete newGroupAccess[groupId];
      setNewGroupAccess(Object.keys(newGroupAccess).length !== 0 ? newGroupAccess : undefined);
    }
  };

  useHotkeys('Enter', () => {
    if ((showShareModal || showShareWithMeModal) && !loading) submitButtonRef?.current?.click();
  });

  useOnClickOutside(searchResultBarRef, onSearchResultsDismiss);
  useOnClickOutside(searchGroupResultBarRef, onGroupSearchResultsDismiss);

  return (
    <Modal open={showShareModal || showShareWithMeModal} onClose={closeModal} loading={loading} mobileFullScreen>
      <form className={css.modalBody} onSubmit={onSubmit}>
        <div className={css.modalTitle}>Share {`“${thisFolder?.name}”`} folder</div>
        <div className={css.contentWrapper}>
          {/* <div className={css.sectionTitle}>People with access</div> */}
          <div className={css.searchWrapper}>
            <SearchInput className={css.search} onChange={onUserSearch} />
            {(userAccessList.length > 0 || groupAccessList.length > 0) && (
              <Tooltip title={renotifyAll ? 'Re-notify all on submit' : 'Re-notify all is off'}>
                <div
                  className={css.renotifyAll}
                  onClick={switchNotifyAll}
                >
                  Re-notify all?
                  <img src={renotifyAll ? BellIconFilled : BellIcon} alt="renotify all" className={css.bellIcon} />
                </div>
              </Tooltip>
            )}
          </div>
          <div ref={searchResultBarRef} className={css.searchResultContainer}>
            {searchedUsers.length > 0 && (
              <div className={css.searchList}>
                {searchedUsers.map((obj, i: number) => (
                  <div onClick={() => onNewUserClick(obj, i)} className={css.user} key={obj.user.id}>
                    <div>
                      <div className={css.username}>{obj.user?.username}</div>
                      {canViewEmails && (<div className={css.email}>{obj.user.email}</div>)}
                    </div>
                    <div>
                      <RolePlate className={css.rolePlate} userRole={obj.role.title} />
                    </div>
                  </div>
                ))}
              </div>
            )}
            {searchText.trim().length > 0 && isUserSearchEmpty && !isUserSearchLoading && (
              <div className={css.noResults}>No Users Found</div>
            )}
          </div>

          <div className={css.collTitle}>
            <div>People with access:</div>
            {thisFolder?.collaborators && thisFolder?.collaborators?.length > 1 && (
              <Tooltip
                style={{ background: 'transparent' }}
                title={
                  <div className={css.collaboratorsList}>
                    {thisFolder?.collaborators.map((u) => (
                      <div className={css.collaborator}>
                        <Avatar avatar={u.avatar_url} name={u.email} />
                        <div className={css.userInfo}>
                          <div> {u?.username} </div>
                          {canViewEmails && (<div className={css.collEmail}>{u.email} </div>)}
                        </div>
                      </div>
                    ))}
                  </div>
                }
              >
                <div className={css.collaborators}>
                  <div className={css.title}>Collaborators:</div>
                  <div className={css.count}>
                    <UsersIcon /> {thisFolder.collaborators.length}
                  </div>
                  {/* <div className={css.collaboratorsList}>
              {thisFolder?.collaborators
                .filter((us) => us.email !== currentUser?.email)
                .map((u) => (
                  <div className={css.collaborator}>
                    <Avatar avatar={u.avatar_url} name={u.email} />
                    <div className={css.userInfo}>
                      <div> {u?.username} </div>
                      <div className={css.collEmail}>{u.email} </div>
                    </div>
                  </div>
                ))}
            </div> */}
                </div>
              </Tooltip>
            )}
          </div>
          <ShareFolderModalUserList
            newUsers={newUsers}
            accessList={userAccessList}
            onNewUserAccessChange={onNewUserAccessChange}
            onUserAccessChange={onUserAccessChange}
            onAccessDelete={handleDeleteAccess}
            onNewUserDelete={handleDeleteNewUser}
            addEntityToRenotify={addEntityToRenotify}
            idsToRenotify={idsToRenotify}
            owner={thisFolder?.owner}
            collaboratorModal
          />

          <div className={css.sectionTitle}>Groups & subgroups with access</div>
          <SearchInput className={css.search} onChange={onGroupSearch} placeHolder="Search groups" />
          <div ref={searchGroupResultBarRef} className={css.searchResultContainer}>
            {searchedGroups.length > 0 && (
              <div className={css.searchList}>
                {searchedGroups.map((group: GROUP, i: number) => (
                  <div onClick={() => onNewGroupClick(group, i)} className={css.user} key={group.id}>
                    <div className={css.username}>{group?.title}</div>
                    <div className={css.groupPeople}>{group?.analysts.length + group.viewers.length} people</div>
                    <div className={css.groupLabel}>Group</div>
                  </div>
                ))}
              </div>
            )}
            {searchGroupText.trim().length > 0 && isGroupSearchEmpty && !isGroupSearchLoading && (
              <div className={css.noResults}>No Groups Found</div>
            )}
          </div>
          <ShareFolderModalGroupList
            accessMapJson={accessMapJson}
            accessList={groupAccessList}
            newGroups={newGroups}
            onGroupAccessChange={onGroupAccessChange}
            onNewGroupAccessChange={onNewGroupAccessChange}
            subGroupAccessList={subGroupAccessList}
            onAccessDelete={handleDeleteAccess}
            onNewGroupDelete={handleDeleteNewGroup}
            addEntityToRenotify={addEntityToRenotify}
            idsToRenotify={idsToRenotify}
          />
          <div className={css.notifyCheckBox}>
            <div className={css.checkboxWrapper30}>
              <span className={css.checkbox}>
                <input type="checkbox" onChange={switchNotify} checked={notify} />
                <svg>
                  <use xlinkHref="#checkbox-30" className={css.checkbox}></use>
                </svg>
              </span>
              <svg xmlns="http://www.w3.org/2000/svg" style={{ display: 'none' }}>
                <symbol id="checkbox-30" viewBox="0 0 22 22">
                  <path
                    fill="none"
                    stroke="currentColor"
                    d="M5.5,11.3L9,14.8L20.2,3.3l0,0c-0.5-1-1.5-1.8-2.7-1.8h-13c-1.7,0-3,1.3-3,3v13c0,1.7,1.3,3,3,3h13 c1.7,0,3-1.3,3-3v-13c0-0.4-0.1-0.8-0.3-1.2"
                  />
                </symbol>
              </svg>
              Notify New Users?
            </div>
          </div>

          {(notify || !!idsToRenotify?.groupIds?.length || !!idsToRenotify?.userIds?.length) && (
            <div className={css.messageText}>
              <textarea
                className={css.messageTextField}
                placeholder="Leave a message for them about content"
                style={{ resize: 'vertical', overflow: 'hidden' }}
                id="messageText"
                cols={33}
                onChange={onMessageTextInput}
                value={messageTextForSharedContent}
              />
            </div>
          )}
        </div>
        <div className={css.actionBtns}>
          <OutlinedButton type="button" kind="default" onClick={closeModal} disabled={loading}>
            <div>Cancel</div>
          </OutlinedButton>
          <ContainedButton
            type={'submit'}
            disabled={
              !(
                usersAccess ||
                groupAccess ||
                newUsersAccess ||
                newGroupAccess ||
                idsToRenotify?.groupIds.length ||
                idsToRenotify?.userIds.length
              ) || loading
            }
          >
            <div ref={submitButtonRef}>Share</div>
          </ContainedButton>
        </div>
      </form>
    </Modal>
  );
};

export default ShareCollaborativeFolderModal;
